import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card, CardBody, Col, Form, Input, Label, Row, FormFeedback, Spinner, Container
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface FormValues {
    name: string;
    gender: string;
    dob: string;
    phone_number: string;
    email: string;
    address: string;
    education: string;
    experience_years: string;
    aadhaar_number: string;
    profile_pic?: FileList;
    kyc_document: FileList;
    police_verification: FileList;
}

export default function AddBystander() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canAdd = hasPermission("bystander-management.add");
    const [saving, setSaving] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>();

    const onSubmit = async (data: FormValues) => {
        setSaving(true);
        try {
            let profilePicFilename = '';
            let kycFilename = '';
            let pvFilename = '';

            if (data.profile_pic && data.profile_pic.length > 0) {
                profilePicFilename = await uploadFile(data.profile_pic[0]);
            }
            if (data.kyc_document && data.kyc_document.length > 0) {
                kycFilename = await uploadFile(data.kyc_document[0]);
            }
            if (data.police_verification && data.police_verification.length > 0) {
                pvFilename = await uploadFile(data.police_verification[0]);
            }

            const payload: any = {
                ...data,
                profile_pic: profilePicFilename || null,
                kyc_document: kycFilename,
                police_verification: pvFilename,
                status: 'Approved', // Requirements: default Approved
                is_verified: true,   // Requirements: default true
                is_enable: true      // Requirements: default true
            };

            await apiPost('bystander/store', payload);
            toast.success('Bystander added successfully');
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/bystander`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error adding bystander');
        } finally {
            setSaving(false);
        }
    };

    if (!canAdd) {
        return (
            <div className="page-body">
                <Breadcrumbs pageTitle="Add Bystander" parent="General" title="Add Bystander" />
                <Container fluid>
                    <Card><CardBody><div className="text-center py-5">No Permission</div></CardBody></Card>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Bystander" parent="General" title="Add Bystander" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Label>Full Name <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{ required: "Name is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Enter Full Name" invalid={!!errors.name} autoComplete="off" />
                                                <FormFeedback>{errors.name?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Gender <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="gender"
                                        control={control}
                                        rules={{ required: "Gender is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.gender}>
                                                    <option value="">Select Gender</option>
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </Input>
                                                <FormFeedback>{errors.gender?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Date of Birth <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="dob"
                                        control={control}
                                        rules={{
                                            required: "DOB is required",
                                            validate: (value) => {
                                                const birthDate = new Date(value);
                                                const today = new Date();
                                                let age = today.getFullYear() - birthDate.getFullYear();
                                                const m = today.getMonth() - birthDate.getMonth();
                                                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                                                    age--;
                                                }
                                                return age >= 18 || "Bystander must be at least 18 years old";
                                            }
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    wrapperClassName="w-100"
                                                    className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                    placeholderText="Select Date of Birth"
                                                    dateFormat="yyyy-MM-dd"
                                                    maxDate={new Date()}
                                                    peekNextMonth
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    autoComplete="off"
                                                />
                                                <FormFeedback style={{ display: errors.dob ? "block" : "none" }}>{errors.dob?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Phone Number <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="phone_number"
                                        control={control}
                                        rules={{ required: "Phone number is required" }}
                                        render={({ field }) => (
                                            <>
                                                <PhoneInput
                                                    country={"in"}
                                                    onlyCountries={["in"]}
                                                    disableDropdown
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    inputProps={{
                                                        name: "phone_number",
                                                        placeholder: "Enter Phone Number",
                                                        autoComplete: "none"
                                                    }}
                                                    inputStyle={{
                                                        width: "100%",
                                                        borderColor: errors.phone_number ? "#dc3545" : "#ced4da"
                                                    }}
                                                />
                                                <FormFeedback style={{ display: errors.phone_number ? "block" : "none" }}>{errors.phone_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Email <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address"
                                            }
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} type="email" placeholder="Email Address" invalid={!!errors.email} autoComplete="off" />
                                                <FormFeedback>{errors.email?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Aadhaar Number <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="aadhaar_number"
                                        control={control}
                                        rules={{ required: "Aadhaar number is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Enter Aadhaar Number" invalid={!!errors.aadhaar_number} autoComplete="off" />
                                                <FormFeedback>{errors.aadhaar_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Education <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="education"
                                        control={control}
                                        rules={{ required: "Education is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.education}>
                                                    <option value="">Select Education</option>
                                                    <option value="School Level">School Level</option>
                                                    <option value="Vocational Training">Vocational Training</option>
                                                    <option value="Technical Diploma">Technical Diploma</option>
                                                    <option value="Undergraduate">Undergraduate</option>
                                                    <option value="Postgraduate">Postgraduate</option>
                                                </Input>
                                                <FormFeedback>{errors.education?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Experience (Years) <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="experience_years"
                                        control={control}
                                        rules={{ required: "Experience is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} type="number" placeholder="Years of Experience" invalid={!!errors.experience_years} autoComplete="off" />
                                                <FormFeedback>{errors.experience_years?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Label>Address <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="address"
                                        control={control}
                                        rules={{ required: "Address is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Complete Address" invalid={!!errors.address} rows={3} />
                                                <FormFeedback>{errors.address?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>Profile Picture <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="profile_pic"
                                        control={control}
                                        rules={{ required: "Profile picture is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*"
                                                    invalid={!!errors.profile_pic}
                                                />
                                                <FormFeedback>{errors.profile_pic?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>KYC Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="kyc_document"
                                        control={control}
                                        rules={{ required: "KYC document (PDF/Image) is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*,.pdf"
                                                    invalid={!!errors.kyc_document}
                                                />
                                                <FormFeedback>{errors.kyc_document?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>Police Verification <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="police_verification"
                                        control={control}
                                        rules={{ required: "Police verification (PDF/Image) is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*,.pdf"
                                                    invalid={!!errors.police_verification}
                                                />
                                                <FormFeedback>{errors.police_verification?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <div className="mt-5">
                                <Btn color="primary" disabled={saving} type="submit" className="px-5">
                                    {saving ? <>Saving <Spinner size="sm" /></> : "Add Bystander"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
